import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { effectiveScore, effectiveRag, priorityFor, ragColor, RAG_BG } from "./shared";

export const PillarDetailModal = ({ pillar, sdf, waterline = 75, open, onClose, assessmentId }) => {
  const [ropa, setRopa] = useState(null);
  const isP1 = pillar?.code === "P1";

  useEffect(() => {
    if (!open || !isP1 || !assessmentId) return;
    api.get(`/ropa/${assessmentId}/summary`)
      .then(({ data }) => setRopa(data))
      .catch(() => setRopa(null));
  }, [open, isP1, assessmentId]);

  if (!pillar) return null;
  const score = effectiveScore(pillar, sdf);
  const rag = effectiveRag(pillar, sdf);
  const gapToWaterline = score != null ? Math.max(0, waterline - score) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md rounded-xl" data-testid="pillar-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#003366]">
            <span className="font-mono">{pillar.code}</span>
            <span className="font-semibold">{pillar.name}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {pillar.code === "P9"
              ? "Score sourced from SDF Module B (obligation readiness) — Rule #4."
              : "Standard V2.2 pillar scoring."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-3xl font-bold text-[#003366]" data-testid="modal-pillar-score">
                {score != null ? `${score}%` : "—"}
              </div>
              <div className="text-[11px] text-slate-500">Current score</div>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${RAG_BG[rag] || RAG_BG.AMBER}`} data-testid="modal-pillar-rag">
              {rag}
            </span>
          </div>

          <div>
            <div className="flex justify-between text-[11px] text-slate-500 mb-1">
              <span>0%</span>
              <span>Waterline {waterline}%</span>
              <span>100%</span>
            </div>
            <div className="relative h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-all"
                style={{ width: `${score ?? 0}%`, backgroundColor: ragColor(rag) }}
              />
              <div className="absolute inset-y-0 w-0.5 bg-[#003366]" style={{ left: `${waterline}%` }} />
            </div>
            {gapToWaterline != null && gapToWaterline > 0 && (
              <div className="mt-1.5 text-[11px] text-slate-500">
                <span className="font-semibold text-[#D32F2F]">{gapToWaterline} pts</span> below audit waterline
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-lg border border-slate-200 p-2.5">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide">Priority window</div>
              <div className="font-semibold text-[#003366] mt-0.5" data-testid="modal-pillar-priority">
                {priorityFor({ ...pillar, rag })}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-2.5">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide">Weight</div>
              <div className="font-semibold text-[#003366] mt-0.5">
                {pillar.weight_pct != null ? `${pillar.weight_pct}% of total` : "—"}
              </div>
            </div>
          </div>

          {isP1 && ropa && (
            <div
              className="rounded-lg border border-[#00897B]/30 bg-[#00897B]/5 p-3 text-xs"
              data-testid="modal-p1-ropa-linkage"
            >
              <div className="text-[#00897B] font-semibold">
                Evidenced by {ropa.approved} approved RoPA record{ropa.approved === 1 ? "" : "s"}
              </div>
              <div className="text-slate-500 mt-0.5">
                {ropa.total} total · coverage {ropa.ropa_coverage_pct}% · {ropa.open_conflicts} open conflict(s)
              </div>
              <Link
                to={`/assessment/${assessmentId}/ropa`}
                onClick={() => onClose?.()}
                data-testid="modal-p1-ropa-link"
                className="inline-block mt-1.5 text-[#008080] hover:underline"
              >
                Open the RoPA register →
              </Link>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
